import { useCallback, useEffect, useRef, useState } from 'react'
import { ExplainMark } from './DevModeContext'

/**
 * SORTING A GRID BY ONE OF ITS COLUMNS — the state, the comparator and the
 * header, in one place.
 *
 * ===========================================================================
 * WHY THIS EXISTS
 *
 * Twelve tables ship in this product. Before 20 Sep 2026 three of them sorted,
 * each having hand-rolled the identical toggle — new column descending, same
 * column flips — in its own file, with its own glyphs (two used ▼▲, one used
 * ▾▴) and its own idea of what `aria-sort` should say when a column is not the
 * sorted one (two said "none", one said nothing at all). The other nine did
 * not sort, including every grid behind `DataGrid`, whose `Column.value` has
 * been documented as "pulls the sortable/summable number out of a row" for
 * as long as it has existed without anything ever sorting by it.
 *
 * That is four implementations of one idea and nine absences of it, which is
 * how a reader ends up learning per-screen whether a heading can be clicked.
 *
 * ===========================================================================
 * THE TWO RULES THAT ARE NOT COSMETIC
 *
 * A MASKED FIGURE IS UNKNOWN, NOT SMALL. Where a reader lacks the column
 * permission the value arrives as null, and null sorts LAST in both
 * directions. Treating it as zero would put the rows the reader can say least
 * about at the top of a descending sort, which is precisely backwards, and
 * ordering by it at all would leak the hidden magnitudes through the row
 * order. The database makes the same refusal from the other side: sorting by
 * a column the caller cannot see is clamped silently and recorded separately
 * as `RefusedSortBy` (012_receivables_transactions.sql:233), because one such
 * request is a misclick and a run down the column list is a probe.
 *
 * A CLIENT-SIDE SORT MUST SEE EVERY ROW. Sorting a server-paged grid in the
 * browser reorders the hundred rows on screen and looks exactly like
 * reordering the report — "the single most convincing wrong answer this
 * screen could give", as TransactionsTable's own header puts it. So a paged
 * grid sorts on the SERVER or not at all, and `useGridSort` is state-only:
 * it never touches rows. Applying the order is the caller's job, because only
 * the caller knows whether it holds the whole population.
 */

/** Which column, and which way. `null` key means the grid's natural order. */
export interface SortState<K extends string = string> {
  key: K | null
  desc: boolean
}

/**
 * The house comparator.
 *
 * Numbers compare numerically, everything else by locale. Nulls and undefined
 * go last in BOTH directions — see the rule above; this is the one part of
 * this file that is about disclosure rather than about tidiness.
 *
 * `tieBreak` keeps a sort stable and repeatable where a column has many equal
 * values: without it, two rows with the same total swap places depending on
 * which order the data arrived in, and a reader who sorts, drills and comes
 * back finds the rows moved for no visible reason.
 */
export function compareBy(
  a: number | string | null | undefined,
  b: number | string | null | undefined,
  desc: boolean,
  tieBreak?: (dir: number) => number,
): number {
  const dir = desc ? -1 : 1
  const aNull = a === null || a === undefined
  const bNull = b === null || b === undefined
  if (aNull && bNull) return tieBreak ? tieBreak(1) : 0
  if (aNull) return 1
  if (bNull) return -1
  let cmp: number
  if (typeof a === 'number' && typeof b === 'number') cmp = (a - b) * dir
  else cmp = String(a).localeCompare(String(b)) * dir
  if (cmp !== 0) return cmp
  return tieBreak ? tieBreak(dir) : 0
}

/**
 * Sort state with the house toggle: a new column opens DESCENDING, clicking
 * the sorted column flips it, and clicking it once more returns the grid to
 * its natural order.
 *
 * Descending-first is what the three tables that already sorted all did, and
 * it is right for this product because almost every sortable column is money
 * or a count, where "show me the biggest" is the question being asked. The
 * third click matters more than it looks: several of these tables open in an
 * order that MEANS something — Monthly Trend is in calendar order, the trend
 * legend mirrors the plotted series, Week Comparison and Salesperson Review
 * lead with the biggest movers — and without a way back, one exploratory
 * click would cost the reader that ordering for the rest of the session.
 *
 * `onChange` fires after every transition so a server-sorted grid can reset
 * to page 1: a new order means page 1 holds different rows.
 */
export function useGridSort<K extends string = string>(
  initial: SortState<K> = { key: null, desc: true },
  onChange?: (next: SortState<K>) => void,
) {
  const [sort, setSort] = useState<SortState<K>>(initial)

  const toggle = useCallback(
    (key: K) => {
      setSort((s) => {
        const next: SortState<K> =
          s.key !== key
            ? { key, desc: true }
            : s.desc
              ? { key, desc: false }
              : { key: initial.key, desc: initial.desc }
        onChange?.(next)
        return next
      })
    },
    [onChange, initial.key, initial.desc],
  )

  /**
   * What a screen reader is told about this column. Always one of the three
   * documented values, never absent — an omitted `aria-sort` on a header that
   * IS sortable reads as "this column has no sort", which is a different
   * statement from "this column is sortable and not currently sorted".
   */
  const ariaSort = useCallback(
    (key: K): 'ascending' | 'descending' | 'none' =>
      sort.key !== key ? 'none' : sort.desc ? 'descending' : 'ascending',
    [sort],
  )

  return { sort, setSort, toggle, ariaSort }
}

/**
 * A column heading that sorts, with the explanation mark beside it.
 *
 * THE MARK IS A SIBLING OF THE BUTTON, NEVER INSIDE IT. A button within a
 * button is invalid HTML that browsers repair by hoisting the inner one out,
 * and stealing the click is no better: sorting is what the reader came to the
 * heading for, and a column that stopped sorting the day somebody documented
 * it would be a strange bug to have to explain.
 *
 * THE `data-head-label` SPAN IS LOAD-BEARING. `gridLayout.minWidthOf` measures
 * it to decide how narrow a column may be dragged, and it must stay
 * `inline-block` to report its own intrinsic width — a block-level label
 * always measures the full column, so the floor becomes the current width and
 * the column can never be narrowed again.
 */
export function SortHeader({
  label,
  active,
  desc,
  onToggle,
  surface,
  measureKey,
  extra,
  hint,
}: {
  label: string
  active: boolean
  desc: boolean
  /** Omit to render a heading that does not sort — the drill/member column. */
  onToggle?: () => void
  surface?: string
  measureKey?: string
  extra?: Record<string, unknown>
  /**
   * A sentence the heading needs on hover, such as what "Mixed Units" means
   * (4 Oct 2026). It rides the tooltip the heading already has rather than a
   * second icon: the report carries ONE "?" affordance, developer mode's, and a
   * look-alike ⓘ beside it would ask the reader to learn two.
   */
  hint?: string
}) {
  const mark = measureKey ? (
    <ExplainMark surface={surface} measureKey={measureKey} label={label} extra={extra} />
  ) : null

  if (!onToggle) {
    return (
      <span data-head-label className="inline-block whitespace-nowrap" title={hint}>
        {label}
        {mark}
      </span>
    )
  }

  return (
    <span data-head-label className="inline-block whitespace-nowrap">
      <button
        type="button"
        onClick={onToggle}
        title={hint ? `${hint}\n\nSort by ${label}` : `Sort by ${label}`}
        className="whitespace-nowrap transition-colors hover:text-(--color-accent)"
      >
        {label}
        {/* aria-hidden: the <th> carries aria-sort, which says the same thing
            properly. A screen reader announcing "black down-pointing triangle"
            after every sorted heading is noise. */}
        {active && <span aria-hidden> {desc ? '▼' : '▲'}</span>}
      </button>
      {mark}
    </span>
  )
}

/* ===========================================================================
 * THE SESSION'S SORTS, ONE PER GRID — so a saved view can carry one.
 *
 * Added 24 Sep 2026. A view is per (report, tab) and captures how the screen
 * is arranged; the sort is part of that arrangement, and until now it was the
 * one part with nowhere to read it from. Every grid holds its sort privately,
 * and in six DIFFERENT shapes: `useGridSort` state here, a `sortKey` of
 * `keyof MatrixNode` plus a boolean in ReceivablesMatrix, a server `@SortBy`
 * string plus a boolean in TransactionsTable and SalesInvoiceLines, one
 * page-level `gridSort` in Material Planning. Lifting six private shapes into
 * six pairs of props through six call sites would have been the larger change
 * AND the more fragile one.
 *
 * So this is the same attachment the column layout already uses: a module
 * store the page reads with `snapshotSort` and writes with `restoreSort`,
 * and one hook that a grid calls to keep its own state in step. Each grid
 * keeps its own shape and its own idea of which keys are legal — which is the
 * point, because only the grid knows that, and a view naming a column that no
 * longer exists (or that this reader may not see) must land as the grid's
 * natural order rather than as a sort by nothing.
 *
 * IT IS DELIBERATELY SESSION-ONLY. A reader's column layout persists silently
 * to the database; a sort does not, because a sort is a question being asked
 * now ("who is worst this week") rather than a way of working. It survives a
 * tab change, it rides inside a named view, and it starts fresh on reload
 * unless the reader saved a view that carries it.
 * ======================================================================== */

const sorts = new Map<string, SortState>()
const sortWatchers = new Map<string, Set<() => void>>()

/** This grid's sort right now, or null if it has never had one this session. */
export function snapshotSort(gridId: string): SortState | null {
  return sorts.get(gridId) ?? null
}

/**
 * Put a sort on a grid from outside — how a saved view applies one.
 *
 * The grid need not be mounted: the value waits, and `useSortMirror` reads it
 * when the grid appears. That matters because a view is applied as soon as the
 * option lists land, which is usually before the rows have arrived and the
 * grid has rendered at all.
 */
export function restoreSort(gridId: string, next: SortState): void {
  sorts.set(gridId, next)
  for (const w of sortWatchers.get(gridId) ?? []) w()
}

/** Everything, on a Login As change — the sorts belonged to the last identity. */
export function forgetAllSorts(): void {
  sorts.clear()
}

/**
 * Keep one grid's own sort state in step with the store.
 *
 * `apply` is where the grid VALIDATES: it is handed whatever was stored and
 * must translate it into its own shape, falling back to its natural order for
 * a key it does not recognise. Nothing here knows what a legal key is.
 *
 * The first publish is skipped on purpose. Without that, a grid mounting after
 * a view had already restored a sort would write its own default straight over
 * it — the restore would be visible for one frame and then vanish, which is
 * the worst of the three possible behaviours.
 */
export function useSortMirror(
  gridId: string,
  current: SortState,
  apply: (next: SortState) => void,
): void {
  const applyRef = useRef(apply)
  applyRef.current = apply
  const published = useRef(false)

  useEffect(() => {
    const w = () => {
      const s = sorts.get(gridId)
      if (s) applyRef.current(s)
    }
    let set = sortWatchers.get(gridId)
    if (!set) {
      set = new Set()
      sortWatchers.set(gridId, set)
    }
    set.add(w)
    w()
    return () => {
      set.delete(w)
      if (set.size === 0) sortWatchers.delete(gridId)
    }
  }, [gridId])

  useEffect(() => {
    if (!published.current) {
      published.current = true
      return
    }
    sorts.set(gridId, current)
  }, [gridId, current.key, current.desc])
}
